import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { fetchTaskById } from 'api/tasks';
import { LinkedResourceText } from 'components/tasks/LinkedResourceText';
import * as HistoryItem from 'components/tasks/single/HistoryItem';
import { useInfiniteTaskHistory } from 'components/tasks/utils/useInfiniteTaskHistory';
import { useUpdateTask } from 'components/tasks/utils/useUpdateTask';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { TaskHistoryItem, TaskLinkable } from 'types/domain';

const TaskHistory = ({ taskId }: { taskId: number }) => {
    const { accountId } = useCurrentAccount();

    const { data: task } = useSuspenseQuery({
        queryKey: accountQueryKeys.taskById(accountId, taskId),
        queryFn: () => fetchTaskById(accountId, taskId),
    });
    const { ref: infiniteRef, data: history } = useInfiniteTaskHistory(task.id);
    const { mutate: updateTask } = useUpdateTask();

    if (history.length === 0) return null;

    return (
        <div className="flex flex-col gap-4 p-8">
            <p className="text-sm font-medium">Activity</p>
            <ul className="flex flex-col">
                {history.map(item => (
                    <li key={`${item.id}_${item.field}`} className="group w-full">
                        <TaskHistoryListItem item={item} />
                    </li>
                ))}

                {task.archived_at && (
                    <li className="flex items-center justify-between gap-4 rounded-md bg-gray-50 p-4">
                        <div>
                            <span className="font-medium">Task archived</span>{' '}
                            {dateFromServer(task.archived_at).toLocaleString(DateTime.DATE_FULL)}
                        </div>
                        <Button
                            variant="none"
                            color="primary"
                            onClick={() => {
                                updateTask({
                                    taskId: task.id,
                                    params: { archived: false },
                                });
                            }}
                        >
                            Unarchive task
                        </Button>
                    </li>
                )}
            </ul>
            <div ref={infiniteRef} />
        </div>
    );
};

const TaskHistoryListItem = ({ item }: { item: TaskHistoryItem }) => {
    const userName = item.user ? `${item.user?.first_name} ${item.user?.last_name}` : '';
    const createdAtDate = dateFromServer(item.created_at).toLocaleString(DateTime.DATE_FULL);

    switch (item.event) {
        case 'created': {
            return (
                <HistoryItem.Root user={item.user}>
                    {item.user ? (
                        <>
                            <HistoryItem.PrimaryText>{userName}</HistoryItem.PrimaryText>{' '}
                            <HistoryItem.SecondaryText>
                                created the task on {createdAtDate}
                            </HistoryItem.SecondaryText>
                        </>
                    ) : (
                        <HistoryItem.SecondaryText>
                            Task created on {createdAtDate}
                        </HistoryItem.SecondaryText>
                    )}
                </HistoryItem.Root>
            );
        }

        case 'archived':
        case 'unarchived':
            return (
                <HistoryItem.Root user={item.user}>
                    {item.user ? (
                        <>
                            <HistoryItem.PrimaryText>{userName}</HistoryItem.PrimaryText>{' '}
                            <HistoryItem.SecondaryText>
                                {item.event} the task on {createdAtDate}
                            </HistoryItem.SecondaryText>
                        </>
                    ) : (
                        <HistoryItem.SecondaryText>
                            Task {item.event} on {createdAtDate}
                        </HistoryItem.SecondaryText>
                    )}
                </HistoryItem.Root>
            );
        case 'links.updated': {
            const oldValues = item.old_values as TaskLinkable[];
            const newValues = item.new_values as TaskLinkable[];
            const isRemoved = oldValues.length > 0;

            return isRemoved ? (
                <HistoryItem.Root user={item.user}>
                    <HistoryItem.PrimaryText>{userName}</HistoryItem.PrimaryText>{' '}
                    <HistoryItem.SecondaryText>removed </HistoryItem.SecondaryText>
                    <HistoryItem.PrimaryText>
                        <LinkedResourceText
                            type={oldValues[0].linkable_type}
                            linkable={oldValues[0].linkable}
                        />
                    </HistoryItem.PrimaryText>
                </HistoryItem.Root>
            ) : (
                <HistoryItem.Root user={item.user}>
                    <HistoryItem.PrimaryText>{userName}</HistoryItem.PrimaryText>{' '}
                    <HistoryItem.SecondaryText>added </HistoryItem.SecondaryText>
                    <HistoryItem.PrimaryText>
                        <LinkedResourceText
                            type={newValues[0].linkable_type}
                            linkable={newValues[0].linkable}
                        />
                    </HistoryItem.PrimaryText>
                </HistoryItem.Root>
            );
        }
        case 'updated':
            return <HistoryItem.UpdatedEvent item={item} />;
    }
};

export { TaskHistory };
